import { LowerCasePipe, TitleCasePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import * as ClaimTrackerModels from '@claim-tracker/common/models';
import isNil from 'lodash/isNil';

import { FormatTelLinkPipe } from '../../shared/pipes/format-tel-link.pipe';

/**
 * Component for displaying the claim representatives.
 *
 * @author Jesse Bloomster e020902
 */
@Component({
	selector: 'claim-tracker-claim-reps',
	standalone: true,
	templateUrl: './claim-reps.component.html',
	imports: [FormatTelLinkPipe, TitleCasePipe, LowerCasePipe]
})
export class ClaimRepsComponent implements OnInit {
	claimReps: ClaimTrackerModels.com.aoins.claims.claim.ClaimRep[];
	heading: string;

	@Input() claim: ClaimTrackerModels.com.aoins.claims.claim.Claim;

	/**
	 * Called when the component is initialized.
	 */
	ngOnInit() {
		this.claimReps = this.claim.claimReps ?? [];
		this.heading = this.determineHeading();
	}

	/**
	 * Get the list of responsibilities for the given claim rep using their index.
	 *
	 * @param index the index of the claim rep in question
	 */
	getResponsibilities = (index: number): string[] => {
		const responsibilities: string[] = [];

		const exposureTypes = this.claimReps[index]?.exposureTypes ?? [];
		const descriptions = ClaimTrackerModels.com.aoins.claims.claim.ExposureTypeDescription;

		exposureTypes.includes('VehicleDamage') && responsibilities.push(descriptions.VEHICLE_DAMAGE);
		exposureTypes.includes('PropertyDamage') && responsibilities.push(descriptions.PROPERTY_DAMAGE);
		exposureTypes.includes('WorkComp_ext') && responsibilities.push(descriptions.WORK_COMP);
		exposureTypes.includes('BodilyInjuryDamage') && responsibilities.push(descriptions.BODILY_INJURY);
		exposureTypes.includes('MedPay') && responsibilities.push(descriptions.MED_PAY);
		(exposureTypes.includes('PI_ext') || exposureTypes.includes('GeneralDamage')) &&
			responsibilities.push(descriptions.OTHER_LIABILITY);

		return responsibilities;
	};

	/**
	 * Determine the heading for the section.
	 */
	private determineHeading = (): string => {
		let headingStr = '';

		switch (true) {
			case this.isOnlyClaimBranches() && this.claimReps.length > 1:
				headingStr = 'Your Claim Branches';
				break;
			case this.isOnlyClaimBranches():
				headingStr = 'Your Claim Branch';
				break;
			case this.claimReps.length > 1:
				headingStr = 'Your Claim Representatives';
				break;
			default:
				headingStr = 'Your Claim Representative';
				break;
		}

		return headingStr;
	};

	/**
	 * Determine if only claim branches exist in the array of claim reps (no claim reps assigned yet).
	 */
	private isOnlyClaimBranches = (): boolean =>
		this.claim?.claimReps?.every(claimRep => isNil(claimRep.lastName)) ?? false;
}
